"use client";

import type { FC } from "react";
import { toAssetImages } from "src/lib/utils";
import {
  MarqueeIconBuilding,
  MarqueeIconFactory,
  MarqueeIconHammer,
  MarqueeIconHardHat,
  MarqueeIconHome,
  MarqueeIconTriangle,
} from "./dathacons-marquee-svg-icons";

import "./dathacons-marquee.css";

export type PartnerMarqueeIconKey =
  | "building"
  | "home"
  | "hardhat"
  | "hammer"
  | "delta"
  | "factory";

export interface PartnerMarqueeItem {
  id: string;
  name: string;
  logo?: string;
  icon?: PartnerMarqueeIconKey;
}

export interface SwiperWrapperDathaconsMarqueeProps {
  heading: string;
  partners: PartnerMarqueeItem[];
}

const ICONS: Record<PartnerMarqueeIconKey, FC<{ className?: string }>> = {
  building: MarqueeIconBuilding,
  home: MarqueeIconHome,
  hardhat: MarqueeIconHardHat,
  hammer: MarqueeIconHammer,
  delta: MarqueeIconTriangle,
  factory: MarqueeIconFactory,
};

function PartnerLogo({ partner }: { partner: PartnerMarqueeItem }) {
  if (partner.logo) {
    return (
      <div
        className="partner-logo group flex h-[80px] min-w-[140px] shrink-0 cursor-pointer items-center justify-center rounded-[12px] border border-[#e8e8e8] bg-[#f5f5f5] px-[25px] py-[15px] transition-all duration-300 md:h-[100px] md:min-w-[200px] md:px-[35px] md:py-[25px] hover:border-[#ffa411] hover:bg-white hover:shadow-lg"
        data-name="Partner logo"
      >
        <img
          src={toAssetImages(partner.logo)}
          alt={partner.name}
          width={220}
          height={64}
          decoding="async"
          className="h-12 w-auto max-w-[min(220px,35vw)] object-contain object-center pointer-events-none md:h-16"
        />
      </div>
    );
  }

  const IconComponent = ICONS[partner.icon ?? "building"] ?? MarqueeIconBuilding;
  return (
    <div
      className="partner-logo group flex h-[80px] min-w-[140px] shrink-0 cursor-pointer items-center justify-center rounded-[12px] border border-[#e8e8e8] bg-[#f5f5f5] px-[25px] py-[15px] transition-all duration-300 md:h-[100px] md:min-w-[200px] md:px-[35px] md:py-[25px] hover:border-[#ffa411] hover:bg-white hover:shadow-lg"
      data-name="Partner logo"
    >
      <div className="partner-logo-content flex items-center gap-2 md:gap-3">
        <div className="text-[#303030] transition-colors duration-300 group-hover:text-[#ffa411]">
          <IconComponent className="partner-logo-icon h-6 w-6 md:h-8 md:w-8" />
        </div>
        <span className="partner-logo-label font-bold whitespace-nowrap text-[#303030] tracking-wide">
          {partner.name}
        </span>
      </div>
    </div>
  );
}

export default function SwiperWrapperDathaconsMarquee({
  heading,
  partners,
}: SwiperWrapperDathaconsMarqueeProps) {
  return (
    <section
      className="relative flex w-full shrink-0 flex-col items-center justify-center overflow-hidden bg-white py-[60px] md:py-[80px]"
      data-name="Partners marquee section"
      aria-labelledby="partners-marquee-heading"
    >
      <div className="mb-10 w-full max-w-[1120px] px-5 md:mb-[60px]">
        <h2
          id="partners-marquee-heading"
          className="text-center text-[32px] font-extrabold leading-[1.3] tracking-tight text-[#303030] md:text-[43px]"
        >
          {heading}
        </h2>
      </div>

      <div className="relative w-full overflow-hidden">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-10 bg-gradient-to-r from-white to-transparent md:w-16"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-10 bg-gradient-to-l from-white to-transparent md:w-16"
          aria-hidden
        />

        <div className="partners-scroll flex w-max items-center gap-[30px] md:gap-[60px]">
          {partners.map((p) => (
            <PartnerLogo key={`a-${p.id}`} partner={p} />
          ))}
          {partners.map((p) => (
            <PartnerLogo key={`b-${p.id}`} partner={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

SwiperWrapperDathaconsMarquee.displayName = "SwiperWrapperDathaconsMarquee";
